import { Amplify } from 'aws-amplify'
import { generateClient } from 'aws-amplify/api'
import {
  confirmResetPassword,
  confirmSignUp,
  confirmUserAttribute,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser as getAmplifyCurrentUser,
  resendSignUpCode,
  resetPassword,
  sendUserAttributeVerificationCode,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  updatePassword,
  updateUserAttributes as amplifyUpdateUserAttributes,
} from 'aws-amplify/auth'
import {
  downloadData,
  getUrl,
  remove,
  uploadData,
} from 'aws-amplify/storage'

let client

function getClient() {
  // Lazily create the GraphQL client so Amplify can be configured first.
  if (!client) {
    client = generateClient()
  }

  return client
}

const authModeMap = {
  AWS_IAM: 'iam',
  AMAZON_COGNITO_USER_POOLS: 'userPool',
  API_KEY: 'apiKey',
}

const storageLevelMap = {
  public: 'guest',
  private: 'private',
  protected: 'protected',
}

const storagePathPrefixMap = {
  public: 'public',
  guest: 'public',
  private: 'private',
  protected: 'protected',
}

const explicitStoragePrefixes = ['public', 'protected', 'private']

function normalizeAuthMode(options = {}) {
  if (!options.authMode) return options
  return {
    ...options,
    authMode: authModeMap[options.authMode] || options.authMode,
  }
}

function createLegacySession(tokens = {}) {
  return {
    idToken: {
      payload: tokens.idToken?.payload || {},
      toString: () => tokens.idToken?.toString?.() || '',
    },
    accessToken: {
      payload: tokens.accessToken?.payload || {},
      toString: () => tokens.accessToken?.toString?.() || '',
    },
  }
}

function hasValidSessionTokens(tokens) {
  return Boolean(tokens?.idToken && tokens?.accessToken)
}

async function createLegacyUser() {
  const [currentUser, attributes, session] = await Promise.all([
    getAmplifyCurrentUser(),
    fetchUserAttributes(),
    fetchAuthSession(),
  ])

  return {
    id: currentUser.userId,
    userId: currentUser.userId,
    username: currentUser.username,
    attributes,
    signInUserSession: createLegacySession(session.tokens),
  }
}

function normalizeStorageOptions(options = {}) {
  const normalizedOptions = {}

  if (options.level) {
    normalizedOptions.accessLevel =
      storageLevelMap[options.level] || options.level
  }

  if (options.contentType) {
    normalizedOptions.contentType = options.contentType
  }

  if (options.onProgress || options.progressCallback) {
    normalizedOptions.onProgress = options.onProgress || options.progressCallback
  }

  return normalizedOptions
}

function normalizeStorageKey(key = '') {
  return key.replace(/^\/+/, '')
}

function hasExplicitStoragePrefix(key = '') {
  return explicitStoragePrefixes.some(
    (prefix) => key === prefix || key.startsWith(`${prefix}/`)
  )
}

async function resolveStoragePath(key, options = {}) {
  const normalizedKey = normalizeStorageKey(key)

  if (!normalizedKey || hasExplicitStoragePrefix(normalizedKey)) {
    return normalizedKey
  }

  const requestedLevel = options.level || options.accessLevel
  const storagePrefix = storagePathPrefixMap[requestedLevel]

  if (!storagePrefix) {
    return normalizedKey
  }

  if (storagePrefix === 'public') {
    return `${storagePrefix}/${normalizedKey}`
  }

  const session = await fetchAuthSession()
  const identityId = session.identityId

  if (!identityId) {
    throw new Error(`No identityId available for storage level "${requestedLevel}"`)
  }

  return `${storagePrefix}/${identityId}/${normalizedKey}`
}

export { Amplify }

export const API = {
  graphql(request) {
    return getClient().graphql(normalizeAuthMode(request))
  },
}

export function graphqlOperation(query, variables) {
  return { query, variables }
}

export const Storage = {
  async put(key, data, options = {}) {
    // A function is Amplify v6's path form ({ identityId }) => path: passed
    // through, Amplify fills in the caller's identity id (and fails without one).
    const path =
      typeof key === 'function' ? key : await resolveStoragePath(key, options)

    return uploadData({
      path,
      data,
      options: normalizeStorageOptions(options),
    }).result
  },

  async get(key, options = {}) {
    const path = await resolveStoragePath(key, options)

    if (options.download) {
      return downloadData({
        path,
        options: normalizeStorageOptions(options),
      }).result
    }

    const result = await getUrl({
      path,
      options: normalizeStorageOptions(options),
    })

    return result.url?.toString?.() || result.url
  },

  async remove(key, options = {}) {
    const path = await resolveStoragePath(key, options)

    return remove({
      path,
      options: normalizeStorageOptions(options),
    }).result
  },
}

export const Auth = {
  // Forces new tokens from the refresh token (not the cached ones), e.g. so a request carries
  // an access token issued now (src/utils/nativePushToken.js).
  async refreshSession() {
    const session = await fetchAuthSession({ forceRefresh: true })
    if (!hasValidSessionTokens(session.tokens)) {
      throw new Error('No valid auth tokens')
    }
  },

  async currentSession() {
    const session = await fetchAuthSession()
    if (!hasValidSessionTokens(session.tokens)) {
      throw new Error('No valid auth tokens')
    }
    return createLegacySession(session.tokens)
  },

  async signIn(username, password) {
    await amplifySignIn({ username, password })
    return createLegacyUser()
  },

  signOut() {
    return amplifySignOut()
  },

  async currentUserInfo() {
    const [currentUser, attributes] = await Promise.all([
      getAmplifyCurrentUser(),
      fetchUserAttributes(),
    ])

    return {
      id: currentUser.userId,
      username: currentUser.username,
      attributes,
    }
  },

  currentAuthenticatedUser() {
    return createLegacyUser()
  },

  updateUserAttributes(_user, attributes) {
    return amplifyUpdateUserAttributes({ userAttributes: attributes })
  },

  signUp({ username, password, attributes }) {
    return amplifySignUp({
      username,
      password,
      options: { userAttributes: attributes },
    })
  },

  confirmSignUp(username, confirmationCode) {
    return confirmSignUp({ username, confirmationCode })
  },

  resendSignUp(username) {
    return resendSignUpCode({ username })
  },

  forgotPassword(username) {
    return resetPassword({ username })
  },

  forgotPasswordSubmit(username, confirmationCode, newPassword) {
    return confirmResetPassword({ username, confirmationCode, newPassword })
  },

  verifyCurrentUserAttribute(userAttributeKey) {
    return sendUserAttributeVerificationCode({ userAttributeKey })
  },

  verifyCurrentUserAttributeSubmit(userAttributeKey, confirmationCode) {
    return confirmUserAttribute({ userAttributeKey, confirmationCode })
  },

  changePassword(_user, oldPassword, newPassword) {
    return updatePassword({ oldPassword, newPassword })
  },
}